/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useId, useState } from 'react'
import {
  AnexoAtendimentoView,
  ArquivoDto,
  AtendimentoView,
  InformacaoAtendimentoView,
  PrescricaoAtendimentoView,
  ProcedimentoAtendimentoView,
  TranscricaoAtendimentoView,
} from '../../../../atendimento/core/_models'
import {
  IconPaths,
  PrescricaoType,
  downloadArquivo,
  formatDateTime,
  showError,
} from '../../../../../../../_metronic/helpers'
import Anotacao from '../Anotacao'
import { GravacoesAtendimento } from '../../../atendimento-video/components/GravacoesAtendimento'
import { useAuth } from '../../../../../auth'
import { temVinculoMedicoAtivoNaEmpresa } from 'app/modules/auth/core/vinculos'
import {
  Button,
  Dispositivos,
  FetchState,
  ListLoading,
  useModal,
} from '../../../../../../components'
import { rotuloNumeroSncr } from 'app/modules/apps/sncr/core/_models'
import {
  downloadAnexo,
  getComprovanteAceite,
  getTranscricoesAtendimento,
} from 'app/modules/apps/atendimento/core/_requests'
import { TranscricaoSection } from '../TranscricaoSection'

export const HistoricoDetalhesModal: FC = () => {
  const { fetchStates } = useModal()
  const { currentUser } = useAuth()

  const { data: atendimento, isLoading } = (fetchStates['atendimento'] ||
    {}) as FetchState<AtendimentoView>

  const isMedico = temVinculoMedicoAtivoNaEmpresa(
    currentUser?.vinculos,
    atendimento?.paciente?.empresa?.id
  )

  const idAtendimento = atendimento?.id
  const sessoesVideo = atendimento?.sessoesVideo ?? []
  const idRotuloSessoesVideo = useId()
  const [comprovanteAceite, setComprovanteAceite] = useState<ArquivoDto | undefined>()

  // O backend responde 404 quando o atendimento não tem aceite registrado ou comprovante
  // gerado; qualquer falha (404, 403 ou erro de rede) apenas mantém a ação fora da tela,
  // sem alerta para o usuário.
  useEffect(() => {
    if (!idAtendimento) return

    let cancelado = false

    getComprovanteAceite(idAtendimento)
      .then((arquivo) => {
        if (!cancelado) setComprovanteAceite(arquivo)
      })
      .catch(() => {
        if (!cancelado) setComprovanteAceite(undefined)
      })

    return () => {
      cancelado = true
    }
  }, [idAtendimento])

  const [transcricoes, setTranscricoes] = useState<TranscricaoAtendimentoView[]>([])
  const [loadingTranscricao, setLoadingTranscricao] = useState<boolean>(
    !!idAtendimento && !!isMedico
  )
  const [erroTranscricao, setErroTranscricao] = useState<boolean>(false)

  useEffect(() => {
    if (!idAtendimento || !isMedico) {
      setTranscricoes([])
      setErroTranscricao(false)
      return
    }

    let cancelado = false
    setLoadingTranscricao(true)
    setErroTranscricao(false)

    getTranscricoesAtendimento(idAtendimento)
      .then((res) => {
        if (!cancelado) setTranscricoes(res)
      })
      .catch(() => {
        if (!cancelado) {
          setTranscricoes([])
          setErroTranscricao(true)
        }
      })
      .finally(() => {
        if (!cancelado) setLoadingTranscricao(false)
      })

    return () => {
      cancelado = true
    }
  }, [idAtendimento, isMedico])

  const getNomeByAnexoId = (
    anexo: AnexoAtendimentoView,
    informacoes: InformacaoAtendimentoView[] = [],
    prescricoes: PrescricaoAtendimentoView[] = [],
    procedimentos: ProcedimentoAtendimentoView[] = []
  ) => {
    for (const info of informacoes) {
      if (info.anexo === anexo.id) {
        return info.descricao
      }
    }

    if (anexo.tipo?.descricao === 'PRESCRICAO') {
      return 'Prescrição'
    }

    for (const procedimento of procedimentos) {
      if (procedimento.anexo === anexo.id) {
        return procedimento.nome
      }
    }

    return anexo.tipo?.descricao
  }

  const medicamentosPrescritos = atendimento?.prescricoes?.filter(
    (prescricao) =>
      prescricao.tipo === PrescricaoType.Especial ||
      prescricao.tipo === PrescricaoType.Medicamento ||
      prescricao.tipo === PrescricaoType.Notificacao ||
      prescricao.tipo === PrescricaoType.Retencao
  )
  const examesPrescritos = atendimento?.prescricoes?.filter(
    (prescricao) => prescricao.tipo === PrescricaoType.Exame
  )
  const orientacoesPrescritas = atendimento?.prescricoes?.filter(
    (prescricao) => prescricao.tipo === PrescricaoType.Orientacoes
  )
  const formulasPrescritos = atendimento?.prescricoes?.filter(
    (prescricao) => prescricao.tipo === PrescricaoType.Formula
  )
  const atestadosPrescritos = atendimento?.prescricoes?.filter(
    (prescricao) => prescricao.tipo === PrescricaoType.Atestado
  )

  const handleDownloadAnexo = async (idAnexo: number) => {
    try {
      const newWindow = window.open('', '_blank')
      const anexo = await downloadAnexo(atendimento?.id, idAnexo, true)
      if (anexo?.url) newWindow?.location.replace(anexo.url)
    } catch (error) {
      showError(error)
    }
  }

  const handleDownloadComprovanteAceite = async () => {
    if (!comprovanteAceite) return

    try {
      await downloadArquivo(comprovanteAceite)
    } catch (error) {
      showError(error)
    }
  }

  return (
    <>
      {isLoading ? (
        <ListLoading />
      ) : (
        <>
          <div className='col-lg-12 col-xl-5'>
            <div className='row mb-5 '>
              <label className='col-lg-8 fw-bold text-muted'>Nome do paciente</label>

              <div className='col-lg-8'>
                <span className='fw-bolder fs-6 text-dark'>{atendimento?.paciente?.nome}</span>
              </div>
            </div>
            <div className='row mb-3'>
              <label className='col-lg-8 fw-bold text-muted'>Criado por</label>

              <div className='col-lg-8'>
                <span className='fw-bolder fs-6 text-dark'>{atendimento?.criador?.nome}</span>
              </div>
            </div>
            <div className='row mb-3'>
              <label className='col-lg-8 fw-bold text-muted'>Responsável</label>

              <div className='col-lg-8'>
                <span className='fw-bolder fs-6 text-dark'>
                  {atendimento?.responsavel?.nome || 'Sem responsável'}
                </span>
              </div>
            </div>
          </div>

          {isMedico && (
            <div className='row mb-3'>
              <label className='col-md-12 fw-bold text-muted'>CID</label>
              {atendimento?.cids ? (
                atendimento.cids.map((c, index) => (
                  <div className='col-lg-12'>
                    <span key={index} className='fw-bold fs-6'>
                      {c.cid.codigo} - {c.cid.descricao}
                    </span>
                  </div>
                ))
              ) : (
                <span className='fw-bold fs-6'>Não definido</span>
              )}
            </div>
          )}
          <div className='row mb-3'>
            <label className='col-md-12 fw-bold text-muted'>Data do Agendamento</label>

            <span className='fw-bold fs-6'>{atendimento?.solicitacaoFormatada}</span>
          </div>
          <div className='row mb-3'>
            <label className='col-md-12 fw-bold text-muted'>Sinais</label>

            <Dispositivos dispositivos={atendimento?.dispositivos || []} />
          </div>

          <div className='row mb-3'>
            <label className='col-md-12 fw-bold text-muted'>Estado</label>

            <div className='col-lg-12'>
              <span className='fw-bold fs-6'>{atendimento?.estado?.descricao}</span>
            </div>
          </div>

          {atendimento?.informacoes && atendimento?.informacoes?.length > 0 && (
            <div className='row mb-3'>
              <label className='col-md-12 fw-bold text-muted'>Informações equipamentos</label>
              <div className='d-flex flex-row-fluid'>
                <ul>
                  {atendimento?.informacoes?.map((info) => (
                    <li>{info.descricao + ' - ' + info.valor + info.unidade}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {atendimento?.anotacoes && atendimento.anotacoes.length > 0 && (
            <div className='row mb-3'>
              <label className='col-md-12 fw-bold text-muted'>Anotações</label>
              <div className='d-flex flex-row-fluid'>
                <ul>
                  {atendimento?.anotacoes?.map((anot) => {
                    return <Anotacao nota={anot} />
                  })}
                </ul>
              </div>
            </div>
          )}

          {atendimento?.prescricoesMemed && atendimento.prescricoesMemed.length > 0 && (
            <div className='row mb-3'>
              <label className='col-lg-8 fw-bold text-muted'>Prescrição/Medicamentos Memed</label>
              <div className='d-flex flex-row-fluid'>
                <ul>
                  {atendimento?.prescricoesMemed?.map((p) =>
                    p.medicamentos?.map((m) => <li>{m.nome + ' - ' + m.posologia}</li>)
                  )}
                </ul>
              </div>
            </div>
          )}
          {atendimento?.prescricoes && atendimento.prescricoes.length > 0 && (
            <>
              {medicamentosPrescritos && medicamentosPrescritos.length > 0 && (
                <div className='row mb-3'>
                  <label className='col-lg-8 fw-bold text-muted'>Prescrição/Medicamentos</label>
                  <div className='col-12'>
                    <div className='row'>
                      {medicamentosPrescritos?.map((p) => (
                        <div key={p.id} className='col-12'>
                          <div className='row'>
                            {p.medicamentos?.map((m) => (
                              <div key={m.id} className='col-md-4 mb-2'>
                                {`${m.medicamento?.nome} - ${m.posologia}`}
                              </div>
                            ))}
                          </div>
                          {p.numeroSncr && (
                            <div className='text-muted fs-8 mb-2'>
                              Nº SNCR {rotuloNumeroSncr(p.numeroSncr, p.tipoReceitaSncr)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {examesPrescritos && examesPrescritos.length > 0 && (
                <div className='row mb-3'>
                  <label className='col-lg-8 fw-bold text-muted'>Prescrição/Exames</label>
                  <div className='d-flex flex-row-fluid'>
                    <div className='row'>
                      {examesPrescritos?.map((p) =>
                        p.exames?.map((exame) => (
                          <div className='col-md-12 mb-2'>
                            <div className='row'>
                              <div className='col-md-12 fw-bolder'>{exame.exame?.nome}</div>
                            </div>
                            <div className='row'>
                              <div className='col-md-12'>{exame.instrucoes}</div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
              {formulasPrescritos && formulasPrescritos.length > 0 && (
                <div className='row mb-3'>
                  <label className='col-lg-8 fw-bold text-muted'>Prescrição/Fórmulas</label>
                  <div className='d-flex flex-row-fluid'>
                    <div className='row'>
                      {formulasPrescritos?.map((p) =>
                        p.formulas?.map((formula) => (
                          <div key={formula.id} className='col-md-12 mb-2'>
                            {`${formula.formula?.nome} - ${formula.formula?.descricao} - ${formula.posologia}`}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
              {orientacoesPrescritas && orientacoesPrescritas.length > 0 && (
                <div className='row mb-3'>
                  <label className='col-lg-8 fw-bold text-muted'>Orientações Médicas</label>
                  <div className='d-flex flex-row-fluid'>
                    <div className='row'>
                      {orientacoesPrescritas?.map((p) =>
                        p.orientacoes?.map((orientacao) => (
                          <div key={orientacao.id} className='col-md-12 mb-2'>
                            {orientacao.orientacoes}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
              {atestadosPrescritos && atestadosPrescritos.length > 0 && (
                <div className='row mb-3'>
                  <label className='col-lg-8 fw-bold text-muted'>Atestados</label>
                  <div className='d-flex flex-row-fluid'>
                    <div className='row'>
                      {atestadosPrescritos?.map((p) => (
                        <div className='col-md-12 mb-2' key={p.atestado?.id}>
                          <div className='row'>
                            <div className='col-md-12 fw-bolder'>{`${p.atestado?.titulo} - ${p.atestado?.dataFormatada}`}</div>
                          </div>
                          <div className='row'>
                            <div className='col-md-12'>{p.atestado?.texto}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {atendimento?.procedimentos && atendimento.procedimentos.length > 0 && (
            <div className='row mb-3'>
              <label className='col-lg-8 fw-bold text-muted'>Exames</label>
              <div className='d-flex flex-row-fluid'>
                <ul>
                  {atendimento?.procedimentos?.map((p) => (
                    <li>{p.nome}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {isMedico && (
            <TranscricaoSection
              isLoading={loadingTranscricao}
              sessoes={transcricoes}
              erro={erroTranscricao}
            />
          )}

          {comprovanteAceite && (
            <div className='row mb-3'>
              <label className='col-lg-8 fw-bold text-muted'>Termo de consentimento</label>
              <div className='col-md-6 mb-2'>
                <Button
                  className='btn-primary btn-sm text-truncate w-100'
                  onClick={handleDownloadComprovanteAceite}
                  icon={IconPaths.CloudDownload}
                >
                  Comprovante de aceite do termo
                </Button>
              </div>
            </div>
          )}

          {atendimento?.anexos && atendimento.anexos.length > 0 && (
            <div className='row mb-3'>
              <label className='col-lg-8 fw-bold text-muted'>Anexos</label>
              {atendimento?.anexos?.map((anexo) => {
                const nome =
                  anexo.descricao ||
                  `${getNomeByAnexoId(
                    anexo,
                    atendimento.informacoes,
                    atendimento?.prescricoes,
                    atendimento.procedimentos
                  )}: ${anexo.url}`

                if (
                  anexo?.tipo?.descricao === 'Atestado de Saúde Ocupacional' &&
                  !anexo?.url?.endsWith('_assinado.pdf')
                ) {
                  return null
                }

                return (
                  <div className='col-md-6 mb-2' key={anexo.id}>
                    <Button
                      className='btn-light btn-active-light-primary btn-sm text-truncate w-100'
                      onClick={() => handleDownloadAnexo(Number(anexo.id))}
                      icon={IconPaths.CloudDownload}
                      title={nome}
                      disabled={!isMedico}
                    >
                      {nome}
                    </Button>
                  </div>
                )
              })}
            </div>
          )}

          {/* Registro de prontuário: visível para todos, sem o gate de isMedico das Gravações. */}
          {atendimento && (
            <div className='row mb-3'>
              <label id={idRotuloSessoesVideo} className='col-lg-8 fw-bold text-muted'>
                Sessões de videochamada
              </label>
              <div className='col-12'>
                {sessoesVideo.length > 0 ? (
                  <>
                    <table
                      className='table table-sm table-row-bordered border-gray-300 table-layout-fixed align-middle mb-2'
                      aria-labelledby={idRotuloSessoesVideo}
                    >
                      <thead>
                        <tr className='fw-semibold text-gray-700 bg-gray-100'>
                          <th scope='col'>Início</th>
                          <th scope='col'>Fim</th>
                          <th scope='col' className='text-end'>
                            Duração
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessoesVideo.map((sessao) => (
                          <tr key={sessao.id}>
                            <td>{formatDateTime(sessao.inicio, sessao.inicioFormatado)}</td>
                            <td>
                              {sessao.fim ? (
                                formatDateTime(sessao.fim, sessao.fimFormatado)
                              ) : (
                                <span className='text-muted'>{sessao.fimFormatado}</span>
                              )}
                            </td>
                            {/* tabular-nums não tem utilitário no tema; inline como em SncrSaldoCell. */}
                            <td className='text-end' style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {sessao.duracaoFormatada ?? (
                                <span
                                  className='text-gray-700'
                                  title='Sem duração: a sessão não tem fim'
                                >
                                  —
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div>
                      <span className='fw-bold text-muted'>Tempo em videochamada:</span>{' '}
                      <span className='fw-bold fs-6'>
                        {atendimento.tempoVideochamadaFormatado ?? '-'}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className='text-muted'>
                    Nenhuma sessão de videochamada registrada para este atendimento.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Gravação só para profissional de saúde da empresa do paciente: sem isMedico a seção
              nem monta, então as URLs assinadas dos vídeos não chegam a ser pedidas. */}
          {idAtendimento && isMedico && (
            <div className='row mb-3'>
              <label className='col-lg-8 fw-bold text-muted'>Gravações</label>
              <div className='d-flex flex-row-fluid'>
                <GravacoesAtendimento idAtendimento={idAtendimento} />
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}
